import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { useBusinessSetup } from '../contexts/business-setup-context';
import {
  getMyBusinessPermissionsService,
  TBusinessPermission,
} from '../services/business-permissions';

/**
 * What the signed-in user may do in their current business, to hide or
 * disable actions the API would refuse with a 403.
 *
 * Display only, and permissive while unknown: until the permissions load
 * (or if loading fails) `can` returns true, so nobody loses an action to a
 * slow request — the API still enforces every permission.
 */
export const useBusinessPermissions = () => {
  const { accessToken } = useAuth();
  const { business } = useBusinessSetup();
  const businessId = business?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['business-permissions', businessId],
    queryFn: () => getMyBusinessPermissionsService(businessId!, accessToken!),
    enabled: !!businessId && !!accessToken,
    staleTime: 5 * 60 * 1000,
  });

  const can = useCallback(
    (permission: TBusinessPermission) =>
      !data ? true : data.permissions.includes(permission),
    [data]
  );

  return { can, role: data?.role ?? null, isLoading };
};
